import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Slider,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useLocation } from "react-router-dom";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const SoftwareSkills = () => {
  const [skills, setSkills] = useState([]);
  const [currentSkill, setCurrentSkill] = useState({ name: "", level: 50 });
  const [editIndex, setEditIndex] = useState(null);
  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  useEffect(() => {
    const saved = localStorage.getItem("softwareSkills");
    if (saved) setSkills(JSON.parse(saved));
  }, []);

  const handleChange = (field, value) => {
    setCurrentSkill((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (index) => {
    setCurrentSkill(skills[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updated = skills.filter((_, i) => i !== index);
    setSkills(updated);
  };

  const handleSaveAndNext = () => {
    // Save skill if name exists
    if (currentSkill.name.trim()) {
      let updatedSkills;
      if (editIndex !== null) {
        updatedSkills = [...skills];
        updatedSkills[editIndex] = currentSkill;
        setEditIndex(null);
      } else {
        updatedSkills = [...skills, currentSkill];
      }

      setSkills(updatedSkills);
      localStorage.setItem("softwareSkills", JSON.stringify(updatedSkills));
      setCurrentSkill({ name: "", level: 50 });
    }

    // Navigate to next section
    const currentSection = "Software";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Software Skills
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, ml: 3 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{
            color: "#002B5B",
            borderBottom: "2px solid #0288D1",
            pb: 1,
            mb: 3,
          }}
        >
          What software skills do you have?
        </Typography>

        {/* Input for skill */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              placeholder="Skill name"
              value={currentSkill.name}
              onChange={(e) => handleChange("name", e.target.value)}
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>

          <Grid item>
            <Slider
              value={currentSkill.level}
              onChange={(e, newVal) => handleChange("level", newVal)}
              min={0}
              max={100}
              step={5}
              valueLabelDisplay="auto"
              sx={{
                width: "200px",
                height: 6,
                color: "#0288D1",
                "& .MuiSlider-thumb": {
                  borderRadius: "4px",
                  width: 20,
                  height: 10,
                  backgroundColor: "#002B5B",
                  "&:hover": { boxShadow: "0 0 0 8px rgba(2, 136, 209, 0.2)" },
                },
                "& .MuiSlider-track": { backgroundColor: "#0288D1" },
                "& .MuiSlider-rail": { backgroundColor: "#B3D9F7" },
                "& .MuiSlider-valueLabel": {
                  backgroundColor: "#002B5B",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Save & Next Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAndNext}
            sx={{
              px: 4,
              fontWeight: "bold",
              backgroundColor: "#002B5B",
              "&:hover": { backgroundColor: "#0288D1" },
              borderRadius: 2,
              boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
            }}
          >
            Save & Next
          </Button>
        </Box>

        {/* Saved skills cards */}
        <Box mt={2}>
          {skills.length > 0 && (
            <Typography
              variant="h6"
              mb={2}
              sx={{
                color: "#002B5B",
                fontWeight: "bold",
                borderBottom: "2px solid #0288D1",
                pb: 1,
              }}
            >
              Saved Skills
            </Typography>
          )}
          {skills.map((skill, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                border: "1px solid #B3D9F7",
                borderLeft: "4px solid #002B5B",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#002B5B" }}>
                  {skill.name}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#B3D9F7",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${skill.level}%`,
                        backgroundColor: "#0288D1",
                        borderRadius: 4,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: "#0D4C8B", minWidth: 40 }}>
                    {skill.level}%
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                <IconButton
                  onClick={() => handleEdit(index)}
                  sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(index)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SoftwareSkills;